import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';
import posthog from '../lib/posthog';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

const isDev = process.env.NODE_ENV === 'development';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(false);
  // null = still determining (profile not yet loaded), true/false = known
  const [onboardingComplete, setOnboardingComplete] = useState(null);
  // true when the user arrived via a password-reset email link
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    // Check if user has seen onboarding
    const seen = localStorage.getItem('nisf_onboarding_seen');
    setHasSeenOnboarding(!!seen);

    let resolved = false;
    let timeoutId = null;

    const finishLoading = (reason) => {
      if (resolved) return;
      resolved = true;
      if (timeoutId) clearTimeout(timeoutId);
      isDev && console.log(`[Auth] loading released (${reason})`);
      setLoading(false);
    };

    // Hard 3s ceiling — if Supabase/network hangs, fall back to logged-out
    // and let the user reach the login screen.
    timeoutId = setTimeout(() => {
      if (!resolved) {
        console.warn('[Auth] init timeout after 3s — defaulting to logged-out');
        setUser(null);
        setProfile(null);
        finishLoading('timeout');
      }
    }, 3000);

    // Helper to fetch or create profile (best-effort, never blocks UI)
    const fetchOrCreateProfile = async (sessionUser) => {
      isDev && console.log('[Auth] fetching profile for', sessionUser.id);
      try {
        const res = await api.getUser(sessionUser.id);
        isDev && console.log('[Auth] profile loaded');
        return res.data;
      } catch (err) {
        console.warn('[Auth] getUser failed, trying ensureProfile:', err?.response?.status, err?.message);
        try {
          const refCode = sessionStorage.getItem('sirati_invite_ref');
          const ensureRes = await api.ensureProfile({
            id: sessionUser.id,
            email: sessionUser.email,
            display_name: sessionUser.user_metadata?.display_name || sessionUser.user_metadata?.full_name || sessionUser.email?.split('@')[0] || 'User',
            gender: sessionUser.user_metadata?.gender || null,
            ...(refCode ? { referred_by_code: refCode } : {}),
          });
          isDev && console.log('[Auth] profile ensured');
          sessionStorage.removeItem('sirati_invite_ref');
          return ensureRes.data;
        } catch (ensureErr) {
          console.error('[Auth] ensureProfile failed:', ensureErr?.response?.status, ensureErr?.message);
          return {
            id: sessionUser.id,
            email: sessionUser.email,
            display_name: sessionUser.user_metadata?.display_name || sessionUser.user_metadata?.full_name || 'User',
            avatar_url: defaultAvatar(sessionUser.user_metadata?.gender),
            bio: '',
            readiness_progress: 0,
            is_admin: false,
          };
        }
      }
    };

    // Check active session — release the loading gate ASAP, then enrich profile in background
    const checkSession = async () => {
      isDev && console.log('[Auth] checking existing session…');
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          isDev && console.log('[Auth] existing session found');
          setUser(session.user);
          finishLoading('existing-session');
          fetchOrCreateProfile(session.user)
            .then((profileData) => {
              if (profileData) {
                setProfile(profileData);
                setOnboardingComplete(!!profileData.onboarding_completed);
                posthog.identify(session.user.id, { email: session.user.email });
              } else {
                setOnboardingComplete(false);
              }
            })
            .catch((err) => {
              console.error('[Auth] background profile fetch failed:', err);
              setOnboardingComplete(false);
            });
        } else {
          isDev && console.log('[Auth] no existing session — logged out');
          setOnboardingComplete(null);
          finishLoading('no-session');
        }
      } catch (error) {
        console.error('[Auth] session check threw:', error?.message);
        try { await supabase.auth.signOut(); } catch { /* noop */ }
        setUser(null);
        setProfile(null);
        finishLoading('session-error');
      }
    };

    checkSession();

    // Listen for auth changes (including post-login)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      isDev && console.log('[Auth] onAuthStateChange:', event, session?.user ? '(user present)' : '(no user)');
      if (session?.user) {
        setUser(session.user);
        finishLoading('auth-state-change');
        // TOKEN_REFRESHED fires on tab refocus and shouldn't clobber in-progress
        // edits (e.g. profile forms) by re-fetching and replacing the profile object.
        if (event === 'TOKEN_REFRESHED') return;
        // PASSWORD_RECOVERY fires when the user follows a password-reset email link.
        // Flag it so the app can show the reset-password form instead of the feed.
        if (event === 'PASSWORD_RECOVERY') { setIsPasswordRecovery(true); return; }
        fetchOrCreateProfile(session.user)
          .then((profileData) => {
            if (profileData) {
              setProfile(profileData);
              setOnboardingComplete(!!profileData.onboarding_completed);
              posthog.identify(session.user.id, { email: session.user.email });
            } else {
              setOnboardingComplete(false);
            }
          })
          .catch((err) => {
            console.error('[Auth] background profile fetch failed:', err);
            setOnboardingComplete(false);
          });
      } else {
        setUser(null);
        setProfile(null);
        setOnboardingComplete(null);
        posthog.reset();
        finishLoading('auth-state-change-null');
      }
    });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email, password, displayName, gender, captchaToken) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName, gender: gender },
        ...(captchaToken ? { captchaToken } : {})
      }
    });
    if (error) throw error;

    // Create profile via API
    if (data.user) {
      try {
        const refCode = sessionStorage.getItem('sirati_invite_ref');
        await api.signup({
          email, password, display_name: displayName, gender: gender,
          ...(refCode ? { referred_by_code: refCode } : {}),
        });
        sessionStorage.removeItem('sirati_invite_ref');
      } catch (err) {
        isDev && console.log('Profile creation handled by auth');
      }
    }
    return data;
  };

  const signIn = async (email, password, captchaToken) => {
    isDev && console.log('[Auth] signIn() called');
    const t0 = performance.now();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined
    });
    const ms = Math.round(performance.now() - t0);
    if (error) {
      console.error(`[Auth] signIn failed in ${ms}ms:`, error.message);
      throw error;
    }
    isDev && console.log(`[Auth] signIn success in ${ms}ms`);
    return data;
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    posthog.reset();
    setUser(null);
    setProfile(null);
    setOnboardingComplete(null);
  };

  const markOnboardingComplete = () => setOnboardingComplete(true);

  const completeOnboarding = () => {
    localStorage.setItem('nisf_onboarding_seen', 'true');
    setHasSeenOnboarding(true);
  };

  const updateProfile = async (updates) => {
    if (!user) return;
    try {
      await api.updateUser(user.id, updates);
      setProfile(prev => ({ ...prev, ...updates }));
    } catch (error) {
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    try {
      const res = await api.getUser(user.id);
      setProfile(res.data);
    } catch (err) {
      console.error('Failed to refresh profile:', err);
    }
  };

  const value = {
    user,
    profile,
    loading,
    hasSeenOnboarding,
    onboardingComplete,
    isPasswordRecovery,
    clearPasswordRecovery: () => setIsPasswordRecovery(false),
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    completeOnboarding,
    markOnboardingComplete,
    updateProfile,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
